/**
 * Trainova AI — TenantForm Component (Provision & Edit Organization)
 */

import { useState, FormEvent } from 'react';

interface TenantFormProps {
  initialData?: {
    name?: string;
    slug?: string;
    primaryColor?: string;
    loginLogoUrl?: string | null;
    topNavLogoUrl?: string | null;
    directLoginUrl?: string | null;
    learnerCanViewOwnResultReport?: boolean;
    videoAnalysisEnabled?: boolean;
    openAiVoiceEnabled?: boolean;
    openAiVoiceEntitled?: boolean;
    mobileNavConfig?: {
      showMobileFooter?: boolean;
      mobileTheme?: 'modern-pill' | 'classic' | 'minimal';
      enableMobileFab?: boolean;
      showAttemptsTab?: boolean;
      showStudioTab?: boolean;
      showReportsTab?: boolean;
    };
  };
  isEdit?: boolean;
  onSubmit: (data: {
    name: string;
    slug: string;
    primaryColor: string;
    loginLogoUrl?: string | null;
    topNavLogoUrl?: string | null;
    directLoginUrl?: string | null;
    learnerCanViewOwnResultReport?: boolean;
    videoAnalysisEnabled?: boolean;
    openAiVoiceEnabled?: boolean;
    openAiVoiceEntitled?: boolean;
    mobileNavConfig?: {
      showMobileFooter?: boolean;
      mobileTheme?: 'modern-pill' | 'classic' | 'minimal';
      enableMobileFab?: boolean;
      showAttemptsTab?: boolean;
      showStudioTab?: boolean;
      showReportsTab?: boolean;
    };
    adminUser?: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
    };
  }) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

export function TenantForm({ initialData, isEdit = false, onSubmit, onCancel, isLoading }: TenantFormProps) {
  const [name, setName] = useState(initialData?.name || '');
  const [slug, setSlug] = useState(initialData?.slug || '');
  const [primaryColor, setPrimaryColor] = useState(initialData?.primaryColor || '#6366F1');
  const [loginLogoUrl, setLoginLogoUrl] = useState(initialData?.loginLogoUrl || '');
  const [topNavLogoUrl, setTopNavLogoUrl] = useState(initialData?.topNavLogoUrl || '');
  const [directLoginUrl, setDirectLoginUrl] = useState(initialData?.directLoginUrl || '');
  const [learnerCanViewOwnResultReport, setLearnerCanViewOwnResultReport] = useState(
    initialData?.learnerCanViewOwnResultReport ?? true
  );
  const [videoAnalysisEnabled, setVideoAnalysisEnabled] = useState(
    initialData?.videoAnalysisEnabled ?? false
  );
  const [openAiVoiceEnabled, setOpenAiVoiceEnabled] = useState(
    initialData?.openAiVoiceEnabled ?? false
  );
  const [openAiVoiceEntitled, setOpenAiVoiceEntitled] = useState(
    initialData?.openAiVoiceEntitled ?? false
  );

  // Mobile Experience & Navigation Template Options
  const [showMobileFooter, setShowMobileFooter] = useState(
    initialData?.mobileNavConfig?.showMobileFooter ?? true
  );
  const [mobileTheme, setMobileTheme] = useState<'modern-pill' | 'classic' | 'minimal'>(
    initialData?.mobileNavConfig?.mobileTheme || 'modern-pill'
  );
  const [enableMobileFab, setEnableMobileFab] = useState(
    initialData?.mobileNavConfig?.enableMobileFab ?? true
  );
  const [showAttemptsTab, setShowAttemptsTab] = useState(
    initialData?.mobileNavConfig?.showAttemptsTab ?? true
  );
  const [showStudioTab, setShowStudioTab] = useState(
    initialData?.mobileNavConfig?.showStudioTab ?? true
  );
  const [showReportsTab, setShowReportsTab] = useState(
    initialData?.mobileNavConfig?.showReportsTab ?? true
  );

  // Admin user (only for creation)
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminFirstName, setAdminFirstName] = useState('');
  const [adminLastName, setAdminLastName] = useState('');

  const handleNameChange = (val: string) => {
    setName(val);
    if (!isEdit) {
      const autoSlug = val
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setSlug(autoSlug);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await onSubmit({
      name: name.trim(),
      slug: slug.trim(),
      primaryColor,
      loginLogoUrl: loginLogoUrl.trim() || null,
      topNavLogoUrl: topNavLogoUrl.trim() || null,
      directLoginUrl: directLoginUrl.trim() || null,
      learnerCanViewOwnResultReport,
      videoAnalysisEnabled,
      openAiVoiceEnabled,
      openAiVoiceEntitled,
      mobileNavConfig: {
        showMobileFooter,
        mobileTheme,
        enableMobileFab,
        showAttemptsTab,
        showStudioTab,
        showReportsTab,
      },
      adminUser: !isEdit && adminEmail
        ? {
            email: adminEmail.trim(),
            password: adminPassword,
            firstName: adminFirstName.trim(),
            lastName: adminLastName.trim(),
          }
        : undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="admin-form" style={{ maxHeight: '85vh', overflowY: 'auto' }}>
      <h3 className="admin-form__title">{isEdit ? 'Edit Organization Details' : 'Provision New Organization'}</h3>

      <div className="admin-form__group">
        <label htmlFor="tenant-name" className="admin-form__label">
          Organization Name *
        </label>
        <input
          id="tenant-name"
          type="text"
          required
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder="Acme Global Enterprise"
          className="admin-form__input"
        />
      </div>

      <div className="admin-form__group">
        <label htmlFor="tenant-slug" className="admin-form__label">
          Organization Slug (URL identifier) *
        </label>
        <input
          id="tenant-slug"
          type="text"
          required
          pattern="[a-z0-9\-]+"
          value={slug}
          disabled={isEdit}
          onChange={(e) => setSlug(e.target.value.toLowerCase())}
          placeholder="acme-global"
          className="admin-form__input"
          style={isEdit ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
        />
        <small className="admin-form__hint">
          {isEdit ? 'Organization identifier slug cannot be changed.' : 'Lowercase alphanumeric and hyphens only (e.g. acme-corp).'}
        </small>
      </div>

      <div className="admin-form__group">
        <label htmlFor="tenant-color" className="admin-form__label">
          Brand Primary Color
        </label>
        <div className="admin-form__color-row">
          <input
            id="tenant-color"
            type="color"
            value={primaryColor}
            onChange={(e) => setPrimaryColor(e.target.value)}
            className="admin-form__color-picker"
          />
          <input
            type="text"
            value={primaryColor}
            onChange={(e) => setPrimaryColor(e.target.value)}
            className="admin-form__input admin-form__input--color-text"
          />
        </div>
      </div>

      {/* ============================================================== */}
      {/* WORKSTREAM 2 & 3: Learner Access & Branding Configuration */}
      {/* ============================================================== */}
      <div className="admin-form__divider" />
      <h4 className="admin-form__subtitle" style={{ color: 'var(--navy)', marginBottom: '14px' }}>
        Learner Access & Branding
      </h4>

      {/* 1. Direct Learner Login URL */}
      <div className="admin-form__group">
        <label htmlFor="direct-login-url" className="admin-form__label">
          Direct Learner Login URL
        </label>
        <input
          id="direct-login-url"
          type="text"
          value={directLoginUrl}
          onChange={(e) => setDirectLoginUrl(e.target.value)}
          placeholder={`/login/${slug || 'org-slug'}`}
          className="admin-form__input"
        />
        <small className="admin-form__hint">
          Custom login path (e.g. <code>/login/{slug || 'org-slug'}</code>) or secure URL. Leave blank for default portal entry.
        </small>
      </div>

      {/* 2. Login Page Logo */}
      <div className="admin-form__group">
        <label htmlFor="login-logo-url" className="admin-form__label">
          Login Page Logo
        </label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            id="login-logo-url"
            type="url"
            value={loginLogoUrl}
            onChange={(e) => setLoginLogoUrl(e.target.value)}
            placeholder="https://cdn.example.com/login-logo.png"
            className="admin-form__input"
            style={{ flex: 1 }}
          />
          {loginLogoUrl && (
            <button
              type="button"
              onClick={() => setLoginLogoUrl('')}
              className="admin-btn admin-btn--secondary"
              style={{ height: '38px', padding: '0 12px', fontSize: '12px' }}
            >
              Clear
            </button>
          )}
        </div>
        <small className="admin-form__hint">
          Logo displayed on the organization login screen. Falls back to default platform logo if empty.
        </small>
        {loginLogoUrl && (
          <div style={{ marginTop: '8px', padding: '10px', background: 'var(--surface)', borderRadius: '8px', border: '1px solid var(--line)', display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)' }}>Preview:</span>
            <img
              src={loginLogoUrl}
              alt="Login Logo Preview"
              style={{ maxHeight: '36px', maxWidth: '160px', objectFit: 'contain' }}
              onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
            />
          </div>
        )}
      </div>

      {/* 3. Top Navigation Logo */}
      <div className="admin-form__group">
        <label htmlFor="topnav-logo-url" className="admin-form__label">
          Top Navigation Logo
        </label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            id="topnav-logo-url"
            type="url"
            value={topNavLogoUrl}
            onChange={(e) => setTopNavLogoUrl(e.target.value)}
            placeholder="https://cdn.example.com/navbar-logo.png"
            className="admin-form__input"
            style={{ flex: 1 }}
          />
          {topNavLogoUrl && (
            <button
              type="button"
              onClick={() => setTopNavLogoUrl('')}
              className="admin-btn admin-btn--secondary"
              style={{ height: '38px', padding: '0 12px', fontSize: '12px' }}
            >
              Clear
            </button>
          )}
        </div>
        <small className="admin-form__hint">
          Logo displayed in the application header and mobile drawer after authentication. Independent of login logo.
        </small>
        {topNavLogoUrl && (
          <div style={{ marginTop: '8px', padding: '10px', background: 'var(--surface)', borderRadius: '8px', border: '1px solid var(--line)', display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)' }}>Preview:</span>
            <img
              src={topNavLogoUrl}
              alt="Top Nav Logo Preview"
              style={{ maxHeight: '32px', maxWidth: '160px', objectFit: 'contain' }}
              onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
            />
          </div>
        )}
      </div>

      {/* 4. Learner Report Access Policy */}
      <div className="admin-form__group" style={{ background: 'var(--surface)', padding: '14px 16px', borderRadius: '10px', border: '1px solid var(--line)' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={learnerCanViewOwnResultReport}
            onChange={(e) => setLearnerCanViewOwnResultReport(e.target.checked)}
            style={{ width: '18px', height: '18px', marginTop: '2px', cursor: 'pointer' }}
          />
          <div>
            <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--navy)' }}>
              Allow learners to view their results
            </span>
            <p style={{ margin: '4px 0 0', fontSize: '12.5px', color: 'var(--muted)', lineHeight: 1.45 }}>
              When enabled, learners can view their own assessment scores, feedback, and evaluation report after completing an assessment. When disabled, only authorized administrators can view the report.
            </p>
          </div>
        </label>
      </div>

      {/* 5. Mobile Experience & Navigation Template */}
      <div
        className="admin-form__group"
        style={{
          background: 'var(--surface)',
          padding: '16px 18px',
          borderRadius: '12px',
          border: '1px solid var(--line)',
          display: 'grid',
          gap: '14px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: 'var(--navy)' }}>
              📱 Mobile Experience & Navigation Template
            </h4>
            <p style={{ margin: '3px 0 0', fontSize: '12px', color: 'var(--muted)' }}>
              Configure responsive mobile navigation, bottom footer tabs, and learner quick-actions.
            </p>
          </div>
          <span
            style={{
              fontSize: '11px',
              fontWeight: 700,
              color: '#2563eb',
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              padding: '2px 8px',
              borderRadius: '999px',
            }}
          >
            Mobile UI
          </span>
        </div>

        {/* Toggle: Show Mobile Footer Bar */}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={showMobileFooter}
            onChange={(e) => setShowMobileFooter(e.target.checked)}
            style={{ width: '18px', height: '18px', marginTop: '2px', cursor: 'pointer' }}
          />
          <div>
            <span style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--ink)' }}>
              Show Mobile Footer (Fixed Bottom Navigation Bar)
            </span>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--muted)' }}>
              Displays the fixed thumb-accessible bottom tab bar on mobile and tablet screens.
            </p>
          </div>
        </label>

        {/* Mobile Template Style Selector */}
        {showMobileFooter && (
          <div style={{ display: 'grid', gap: '6px', paddingLeft: '28px' }}>
            <label htmlFor="mobile-theme-select" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ink)' }}>
              Mobile Footer Template Style
            </label>
            <select
              id="mobile-theme-select"
              value={mobileTheme}
              onChange={(e) => setMobileTheme(e.target.value as any)}
              className="admin-form__input"
              style={{ fontSize: '13px', height: '38px', padding: '0 10px', background: 'var(--white)' }}
            >
              <option value="modern-pill">✨ Modern Floating Pill (Recommended — Rounded Dock with Blur)</option>
              <option value="classic">🏛️ Classic Docked Bar (Edge-to-edge full width)</option>
              <option value="minimal">⚡ Minimalist Flat (Compact slimline bar)</option>
            </select>
          </div>
        )}

        {/* Toggle: Mobile Floating Action Button (FAB) */}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={enableMobileFab}
            onChange={(e) => setEnableMobileFab(e.target.checked)}
            style={{ width: '18px', height: '18px', marginTop: '2px', cursor: 'pointer' }}
          />
          <div>
            <span style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--ink)' }}>
              Show Floating Action Button (FAB) on Dashboard
            </span>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--muted)' }}>
              Floating button above footer allowing learners to quickly jump to and start their next simulation.
            </p>
          </div>
        </label>

        {/* Navigation Tabs Options */}
        {showMobileFooter && (
          <div style={{ paddingLeft: '28px', display: 'grid', gap: '8px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ink)' }}>
              Visible Mobile Tabs:
            </span>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showAttemptsTab}
                  onChange={(e) => setShowAttemptsTab(e.target.checked)}
                />
                Attempts Tab
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showStudioTab}
                  onChange={(e) => setShowStudioTab(e.target.checked)}
                />
                Studio Tab
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showReportsTab}
                  onChange={(e) => setShowReportsTab(e.target.checked)}
                />
                Reports Tab
              </label>
            </div>
          </div>
        )}
      </div>

      <div className="admin-form__divider" />

      {/* Advanced Telemetry & Premium Feature Governance */}
      <div style={{ display: 'grid', gap: '14px', background: 'var(--surface)', padding: '16px', borderRadius: '10px', border: '1px solid var(--line)' }}>
        <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)' }}>
          Simulation Features & AI Engine Entitlements
        </span>

        {/* Toggle: Video Analysis */}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={videoAnalysisEnabled}
            onChange={(e) => setVideoAnalysisEnabled(e.target.checked)}
            style={{ width: '18px', height: '18px', marginTop: '2px', cursor: 'pointer' }}
          />
          <div>
            <span style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--ink)' }}>
              Enable Browser-Based Video Analysis (face-api.js)
            </span>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--muted)' }}>
              Permits simulations to analyze facial presence locally in the learner's browser. Raw camera frames never leave the device.
            </p>
          </div>
        </label>

        {/* Toggle: Premium OpenAI Voice */}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={openAiVoiceEnabled}
            onChange={(e) => setOpenAiVoiceEnabled(e.target.checked)}
            style={{ width: '18px', height: '18px', marginTop: '2px', cursor: 'pointer' }}
          />
          <div>
            <span style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--ink)' }}>
              Enable Premium OpenAI Voice (TTS)
            </span>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--muted)' }}>
              Allows simulations to select realistic OpenAI Audio Speech voices. Notice: may incur additional provider API usage charges.
            </p>
          </div>
        </label>

        {/* Toggle: OpenAI Voice Entitlement (Platform Admin) */}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={openAiVoiceEntitled}
            onChange={(e) => setOpenAiVoiceEntitled(e.target.checked)}
            style={{ width: '18px', height: '18px', marginTop: '2px', cursor: 'pointer' }}
          />
          <div>
            <span style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--ink)' }}>
              OpenAI Voice Entitlement (License Grant)
            </span>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--muted)' }}>
              Platform license entitlement allowing this organization to use OpenAI Voice features.
            </p>
          </div>
        </label>
      </div>

      {!isEdit && (
        <>
          <div className="admin-form__divider" />

          <h4 className="admin-form__subtitle">Initial Organization Admin (Optional)</h4>

          <div className="admin-form__row">
            <div className="admin-form__group">
              <label htmlFor="admin-fname" className="admin-form__label">
                First Name
              </label>
              <input
                id="admin-fname"
                type="text"
                value={adminFirstName}
                onChange={(e) => setAdminFirstName(e.target.value)}
                placeholder="Jane"
                className="admin-form__input"
              />
            </div>
            <div className="admin-form__group">
              <label htmlFor="admin-lname" className="admin-form__label">
                Last Name
              </label>
              <input
                id="admin-lname"
                type="text"
                value={adminLastName}
                onChange={(e) => setAdminLastName(e.target.value)}
                placeholder="Admin"
                className="admin-form__input"
              />
            </div>
          </div>

          <div className="admin-form__group">
            <label htmlFor="admin-email" className="admin-form__label">
              Admin Email
            </label>
            <input
              id="admin-email"
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="admin@acme.com"
              className="admin-form__input"
            />
          </div>

          {adminEmail && (
            <div className="admin-form__group">
              <label htmlFor="admin-password" className="admin-form__label">
                Admin Password *
              </label>
              <input
                id="admin-password"
                type="password"
                required={Boolean(adminEmail)}
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                placeholder="Min 12 characters"
                className="admin-form__input"
              />
            </div>
          )}
        </>
      )}

      <div className="admin-form__actions" style={{ marginTop: '20px' }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="admin-btn admin-btn--secondary"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading || !name || !slug || (!isEdit && Boolean(adminEmail) && !adminPassword)}
          className="admin-btn admin-btn--primary"
        >
          {isLoading ? (isEdit ? 'Saving…' : 'Provisioning…') : (isEdit ? 'Save Changes' : 'Provision Organization')}
        </button>
      </div>
    </form>
  );
}
